import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import BriefReviewModal from "@/components/BriefReviewModal";
import { listProspects, createProspect, updateProspect, deleteProspect, createDemo, getDemo, getDemoSiteUrl, getDemoPublicUrl, generateOutreach, unsubscribeUrl, verifyProspectWebsite } from "@/lib/api";
import { isUkMobile } from "@/lib/channels";
import { toast } from "sonner";
import { Phone, ExternalLink, X, Trash2, Ban, Copy, ChevronRight, ChevronLeft, Sparkles, Loader2, Instagram, Facebook, MessageCircle, Mail, Plus, LayoutGrid, Table2, CalendarPlus, CalendarCheck, Eye, Search } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

const STAGES = [
  { id: "found",           label: "Found",           color: "#94a3b8" },
  { id: "demo_built",      label: "Demo Built",       color: "#3b82f6" },
  { id: "outreach_sent",   label: "Outreach Sent",    color: "#f59e0b" },
  { id: "no_answer",       label: "No Answer",        color: "#eab308" },
  { id: "in_conversation", label: "In Conversation",  color: "#f97316" },
  { id: "demo_shared",     label: "Demo Shared",      color: "#8b5cf6" },
  { id: "interested",      label: "Interested",       color: "#10b981" },
  { id: "deposit_paid",    label: "Deposit Paid",     color: "#22c55e" },
  { id: "site_live",       label: "Site Live",        color: "#FF3B30" },
  { id: "low_value",       label: "Low Value",        color: "#71717a" },
  { id: "declined",        label: "Declined (No)",    color: "#ef4444" },
];

// "declined" is a one-way exit, not a normal pipeline stage — never a board column,
// and never reachable via the board's forward/back stage-arrow navigation.
const BOARD_STAGES = STAGES.filter(s => s.id !== "declined");
const BOARD_STAGE_IDS = BOARD_STAGES.map(s => s.id);
const STAGE_IDS = STAGES.map(s => s.id);
const VIEW_STORAGE_KEY = "pipelineView";

function waLink(phone, message) {
  const clean = (phone || "").replace(/[\s\-().+]/g, "").replace(/^0/, "44");
  const base = `https://wa.me/${clean}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

function stepFor(prospect, channel) {
  return prospect?.outreach?.sequence?.find(s => s.channel === channel);
}

// The model is never told the operator's real name, so it stands in with the literal
// token [YOUR NAME] on call scripts and email sign-offs — highlight it so it can't be
// missed and accidentally read aloud or sent as-is.
function HighlightYourName({ text }) {
  const parts = (text || "").split(/(\[YOUR NAME\])/g);
  return parts.map((part, i) =>
    part === "[YOUR NAME]"
      ? <mark key={i} className="px-1 rounded font-semibold" style={{ backgroundColor: "#fde68a", color: "#78350f" }}>{part}</mark>
      : <span key={i}>{part}</span>
  );
}

const BLANK_FORM = { name: "", trade: "", borough: "", address: "", phone: "", instagram: "" };

// ── Calendar helpers ──────────────────────────────────────────────────────────

/** Return a Date that is `days` calendar days from today (midnight local time). */
function addDays(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
}

/** Format a Date as YYYYMMDD (all-day iCal / Google date string). */
function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

/** Open a pre-filled Google Calendar event in a new tab. */
function openGoogleCalendar({ title, date, details, location }) {
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${toDateStr(date)}/${toDateStr(next)}`,
    details,
    location: location || "",
  });
  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, "_blank", "noopener,noreferrer");
}

/** Trigger a .ics file download for Apple Calendar / Outlook / any calendar app. */
function downloadIcs({ title, date, details, location }) {
  const uidSuffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}`;
  const uid = `managerly-${Date.now()}-${uidSuffix}@the-managerly.com`;
  const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const dateStr = toDateStr(date);
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  const nextStr = toDateStr(next);
  // iCalendar text escaping per RFC 5545 §3.3.11: backslashes first, then semicolons, commas, and newlines.
  const escapeIcs = (s) => (s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
  const safeDetails = escapeIcs(details);
  const safeTitle = escapeIcs(title);
  const safeLocation = escapeIcs(location);
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Managerly//Pipeline//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${dateStr}`,
    `DTEND;VALUE=DATE:${nextStr}`,
    `SUMMARY:${safeTitle}`,
    `DESCRIPTION:${safeDetails}`,
    `LOCATION:${safeLocation}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = title.replace(/[^a-z0-9]/gi, "_") || "calendar_event";
  a.download = `${safeName}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build the calendar event payload for a given outreach step.
 * `step.day` is the number of days from today to schedule the reminder.
 */
function buildCalendarEvent(step, prospectName, borough, message) {
  const channelLabel = {
    whatsapp: "WhatsApp",
    instagram: "Instagram DM",
    facebook: "Facebook message",
    email: "Email",
    call: "Call",
  }[step.channel] || step.channel;
  return {
    title: `Follow up with ${prospectName} — ${channelLabel}`,
    date: addDays(step.day),
    details: message || "",
    location: borough || "",
  };
}

/** Dropdown with Google Calendar + .ics download options for a follow-up step. */
function CalendarDropdown({ step, prospectName, borough, message }) {
  const event = buildCalendarEvent(step, prospectName, borough, message);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          title={`Add Day ${step.day} follow-up to calendar`}
          className="inline-flex items-center gap-1 font-mono text-[10px] px-2 py-0.5 hover:bg-gray-100 rounded"
          style={{ color: "#6b7280", border: "1px solid #e5e7eb" }}
        >
          <CalendarPlus className="w-3 h-3" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={4}
          className="z-[100] min-w-[160px] rounded shadow-lg py-1"
          style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}
        >
          <DropdownMenu.Item
            onSelect={() => openGoogleCalendar(event)}
            className="flex items-center gap-2 px-3 py-2 font-mono text-[11px] cursor-pointer outline-none hover:bg-gray-50"
            style={{ color: "#374151" }}
          >
            <CalendarCheck className="w-3.5 h-3.5" style={{ color: "#4285F4" }} />
            Google Calendar
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => { downloadIcs(event); toast.success("Calendar file downloaded"); }}
            className="flex items-center gap-2 px-3 py-2 font-mono text-[11px] cursor-pointer outline-none hover:bg-gray-50"
            style={{ color: "#374151" }}
          >
            <CalendarPlus className="w-3.5 h-3.5" style={{ color: "#6b7280" }} />
            Download .ics
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export default function Pipeline() {
  const [view, setView] = useState(() => {
    try {
      const storedView = localStorage.getItem(VIEW_STORAGE_KEY);
      return storedView === "table" || storedView === "board" ? storedView : "table";
    } catch {
      return "table";
    }
  });
  const [prospects, setProspects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTrade, setFilterTrade] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingOutreach, setGeneratingOutreach] = useState(false);
  const [notes, setNotes] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [facebook, setFacebook] = useState("");
  const [value, setValue] = useState(500);
  const [buildingId, setBuildingId] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [reviewDemo, setReviewDemo] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(() => {
    listProspects().then(setProspects).catch(() => toast.error("Could not load pipeline"));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {}
  }, [view]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.trade.trim()) return;
    setAddSaving(true);
    try {
      const handle = addForm.instagram.trim().replace(/^@/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//i, "");
      const payload = {
        ...addForm,
        instagram: handle ? `https://instagram.com/${handle}` : "",
      };
      const p = await createProspect(payload);
      setProspects(ps => [p, ...ps]);
      setAddOpen(false);
      setAddForm(BLANK_FORM);
      toast.success("Prospect added");
    } catch {
      toast.error("Could not add prospect");
    } finally {
      setAddSaving(false);
    }
  };

  const openProspect = (p) => {
    setSelected(p);
    setNotes(p.notes || "");
    setContactName(p.contact_name || "");
    setContactEmail(p.contact_email || "");
    setInstagram(p.instagram || "");
    setFacebook(p.facebook || "");
    setValue(p.value ?? 500);
    if (p.demo_id) getDemo(p.demo_id).then(setSelectedDemo).catch(() => setSelectedDemo(null));
    else setSelectedDemo(null);
  };

  // Poll while the selected demo's site is still publishing to the CDN
  useEffect(() => {
    if (!selected?.demo_id || selectedDemo?.public_url) return;
    const interval = setInterval(() => {
      getDemo(selected.demo_id).then(setSelectedDemo).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [selected?.demo_id, selectedDemo?.public_url]);

  const patch = useCallback(async (id, updates) => {
    setSaving(true);
    try {
      const updated = await updateProspect(id, updates);
      setProspects(ps => ps.map(p => p.id === id ? updated : p));
      if (selected?.id === id) setSelected(updated);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [selected]);

  const moveStage = async (e, p, dir) => {
    e.stopPropagation();
    const idx = BOARD_STAGE_IDS.indexOf(p.stage);
    const next = BOARD_STAGE_IDS[idx + dir];
    if (!next) return;
    await patch(p.id, { stage: next });
  };

  const savePanel = async () => {
    if (!selected) return;
    await patch(selected.id, {
      notes, contact_name: contactName, contact_email: contactEmail,
      value, instagram, facebook,
    });
    toast.success("Saved");
  };

  const handleGenerateOutreach = async () => {
    if (!selected) return;
    setGeneratingOutreach(true);
    try {
      const updated = await generateOutreach(selected.id);
      setProspects(ps => ps.map(p => p.id === updated.id ? updated : p));
      setSelected(updated);
      toast.success("Outreach generated");
    } catch {
      toast.error("Generation failed");
    } finally {
      setGeneratingOutreach(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this prospect?")) return;
    try {
      await deleteProspect(id);
      setProspects(ps => ps.filter(p => p.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  // Client said no: drops out of the pipeline (like a delete) but the record is kept
  // so this exact business is excluded from future discover/bulk-discover results.
  const decline = async (p) => {
    if (!window.confirm(`Mark ${p.name} as declined? It'll drop off the pipeline and won't be suggested again in future searches.`)) return;
    await patch(p.id, { stage: "declined" });
    if (selected?.id === p.id) setSelected(null);
  };

  // Build now only generates the brief (cheap-ish) and stops — the expensive full site build
  // waits for BriefReviewModal's approval, so a wrong brief never burns a full HTML generation.
  const buildDemo = async (p) => {
    setBuildingId(p.id);
    try {
      const demo = await createDemo({
        name: p.name, trade: p.trade, borough: p.borough,
        address: p.address, phone: p.phone, rating: p.rating,
        reviews: p.reviews, website: p.website,
      });
      setReviewDemo({ demo, prospect: p });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const website = typeof detail === "object" ? detail?.website : null;
      if (website) {
        await patch(p.id, { website });
        toast.warning(`Already has a site: ${website}`);
      } else {
        const message = typeof detail === "string" ? detail : detail?.message || e?.message || "unknown error";
        toast.error("Brief generation failed: " + message);
      }
    } finally { setBuildingId(null); }
  };

  const handleBriefApproved = async (built, comment) => {
    const p = reviewDemo.prospect;
    const updates = { demo_id: built.id, stage: "demo_built" };
    if (comment) updates.notes = comment;
    await patch(p.id, updates);
    setSelectedDemo(built);
    setReviewDemo(null);
    toast.success("Demo built");
  };

  // Google Places' self-reported websiteUri (what discover/bulk trawl trust) is often
  // empty even when a real site exists — this runs a live web-search check on demand
  // instead of before every discovery result.
  const verifyWebsite = async (p) => {
    setVerifyingId(p.id);
    try {
      const { website } = await verifyProspectWebsite(p.id);
      if (website) {
        setProspects(ps => ps.map(x => x.id === p.id ? { ...x, website } : x));
        if (selected?.id === p.id) setSelected(s => ({ ...s, website }));
        toast.warning(`Already has a site: ${website}`);
      } else {
        toast.success("No live website found — clear to build");
      }
    } catch (e) {
      toast.error("Verification failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally { setVerifyingId(null); }
  };

  const stageColor = (id) => STAGES.find(s => s.id === id)?.color ?? "#94a3b8";
  const stageLabel = (id) => STAGES.find(s => s.id === id)?.label ?? id;

  const totalValue = prospects
    .filter(p => ["deposit_paid", "site_live"].includes(p.stage))
    .reduce((a, p) => a + (p.value || 0), 0);

  const pipelineValue = prospects
    .filter(p => !["found", "site_live"].includes(p.stage))
    .reduce((a, p) => a + (p.value || 0), 0);

  const tradeOptions = [...new Set(prospects.map(p => p.trade).filter(Boolean))].sort();

  const filtersActive = Boolean(searchQuery || filterTrade || filterStage);

  const filteredProspects = prospects.filter(p => {
    // Special filter modes: show only DNC or only declined
    if (filterStage === "dnc") return p.dnc;
    if (filterStage === "declined") return p.stage === "declined";
    // Default: exclude DNC and declined, then apply trade/stage/search filters
    if (p.dnc || p.stage === "declined") return false;
    if (filterTrade && p.trade !== filterTrade) return false;
    if (filterStage && p.stage !== filterStage) return false;
    if (searchQuery && !(p.name || "").toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const clearFilters = () => { setSearchQuery(""); setFilterTrade(""); setFilterStage(""); };

  // Inject actual demo link and unsubscribe link into outreach copy
  const outreachWithLink = (text) => {
    if (!text) return text;
    let out = text;
    if (selected?.demo_id) {
      const url = getDemoPublicUrl(selectedDemo, selected.demo_id);
      out = out.replace(/\{\{demo_link\}\}/g, url);
    }
    if (selected?.id) {
      out = out.replace(/\{\{unsubscribe_link\}\}/g, unsubscribeUrl(selected.id));
    }
    return out;
  };

  const canWhatsapp = isUkMobile(selected?.phone);
  const canInstagram = Boolean(selected?.instagram);
  const canFacebook = Boolean(selected?.facebook);
  const canEmail = Boolean(selected?.contact_email);
  const canCall = Boolean(selected?.phone);
  const hasOutreachChannel = canWhatsapp || canInstagram || canFacebook || canEmail || canCall;
  const isPublished = Boolean(selectedDemo?.public_url);

  const CHANNEL_META = {
    whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
    instagram: { label: "Instagram DM", icon: Instagram, color: "#E1306C" },
    facebook: { label: "Facebook message", icon: Facebook, color: "#1877F2" },
    email: { label: "Email", icon: Mail, color: "#374151" },
    call: { label: "Call script", icon: Phone, color: "#374151" },
  };

  const channelOpenLink = (channel, message) => {
    if (selected.dnc) return undefined;
    if (channel === "whatsapp") return waLink(selected.phone, message);
    if (channel === "instagram") return selected.instagram;
    if (channel === "facebook") return selected.facebook;
    if (channel === "email") {
      // Generated email copy is one string shaped "Subject: ...\n\n<body>" — split it so the
      // subject actually lands in the mailto subject field instead of appearing as the first
      // line of the email body.
      const params = new URLSearchParams();
      let body = message || "";
      const m = body.match(/^Subject:\s*(.+?)\r?\n\r?\n([\s\S]*)$/i);
      if (m) {
        params.set("subject", m[1].trim());
        body = m[2];
      }
      if (body) params.set("body", body);
      const query = params.toString();
      return `mailto:${selected.contact_email}${query ? `?${query}` : ""}`;
    }
    if (channel === "call") return `tel:${selected.phone}`;
    return undefined;
  };

  return (
    <div style={{ backgroundColor: "#f4f4f5", minHeight: "100vh", overflowX: "hidden" }}>
      <Nav />

      {/* Header */}
      <div style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4">
          {/* Row 1: title + add button */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: "#FF3B30" }}>CRM · PIPELINE</p>
              <h1 className="font-display text-3xl tracking-tight" style={{ color: "#111827" }}>Pipeline</h1>
            </div>
            <button onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors"
              style={{ backgroundColor: "#111827", color: "#ffffff" }}>
              <Plus className="w-3.5 h-3.5" /> Add prospect
            </button>
          </div>
          {/* Row 2: view toggle + stats */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex rounded overflow-hidden" style={{ border: "1px solid #e5e7eb" }}>
              <button onClick={() => setView("table")}
                className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors"
                style={view === "table" ? { backgroundColor: "#111827", color: "#ffffff" } : { backgroundColor: "#ffffff", color: "#9ca3af" }}>
                <Table2 className="w-3.5 h-3.5" /> Table
              </button>
              <button onClick={() => setView("board")}
                className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors"
                style={view === "board" ? { backgroundColor: "#111827", color: "#ffffff" } : { backgroundColor: "#ffffff", color: "#9ca3af" }}>
                <LayoutGrid className="w-3.5 h-3.5" /> Board
              </button>
            </div>
            <div className="flex gap-6">
              <div className="text-right">
                <div className="font-mono text-xl font-bold" style={{ color: "#111827" }}>£{pipelineValue.toLocaleString()}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "#9ca3af" }}>In pipeline</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-xl font-bold" style={{ color: "#16a34a" }}>£{totalValue.toLocaleString()}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "#9ca3af" }}>Closed</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-xl font-bold" style={{ color: "#111827" }}>{prospects.length}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "#9ca3af" }}>Prospects</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
        <div className="max-w-screen-2xl mx-auto px-6 py-3 flex items-center gap-3 flex-wrap">
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name…"
            className="px-3 py-2 rounded font-mono text-[11px] outline-none w-56"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
          />
          <select value={filterTrade} onChange={e => setFilterTrade(e.target.value)}
            className="px-3 py-2 rounded font-mono text-[11px] uppercase tracking-wider outline-none"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: filterTrade ? "#111827" : "#9ca3af" }}>
            <option value="">All trades</option>
            {tradeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStage} onChange={e => setFilterStage(e.target.value)}
            className="px-3 py-2 rounded font-mono text-[11px] uppercase tracking-wider outline-none"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: filterStage ? "#111827" : "#9ca3af" }}>
            <option value="">All stages</option>
            {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            <option value="dnc">DNC (opted out)</option>
          </select>
          {filtersActive && (
            <button onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:opacity-70"
              style={{ color: "#6b7280" }}>
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          {filtersActive && (
            <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#9ca3af" }}>
              {filteredProspects.length} of {prospects.length}
            </span>
          )}
        </div>
      </div>

      {/* Kanban */}
      {view === "board" && (
      <div className="overflow-x-auto px-6 py-6">
        <div className="flex gap-3" style={{ minWidth: "max-content" }}>
          {BOARD_STAGES.map(stage => {
            const cards = filteredProspects.filter(p => p.stage === stage.id);
            return (
              <div key={stage.id} style={{ width: "200px", flexShrink: 0 }}>
                <div className="flex items-center justify-between px-3 py-2 mb-2 rounded"
                  style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "#374151" }}>{stage.label}</span>
                  </div>
                  <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: cards.length ? stage.color + "20" : "transparent", color: cards.length ? stage.color : "#d1d5db" }}>
                    {cards.length}
                  </span>
                </div>

                <div className="space-y-2">
                  {cards.map(p => (
                    <div key={p.id} onClick={() => openProspect(p)}
                      className="rounded cursor-pointer transition-shadow hover:shadow-md"
                      style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderLeft: `3px solid ${stage.color}` }}>
                      <div className="px-3 pt-3 pb-2">
                        <div className="flex items-start justify-between gap-1 mb-1">
                          <div className="font-display text-sm leading-tight" style={{ color: "#111827" }}>{p.name}</div>
                          <div className="flex items-center gap-1 shrink-0">
                            {p.candidate_score != null && (
                              <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                                title="Candidacy score — auto-matched from Google Maps, unverified"
                                style={p.candidate_score >= 70
                                  ? { backgroundColor: "#FF3B30", color: "#ffffff" }
                                  : p.candidate_score >= 40
                                  ? { backgroundColor: "#ffeeed", color: "#FF3B30" }
                                  : { backgroundColor: "#f3f4f6", color: "#6b7280" }}>
                                {p.candidate_score}
                              </span>
                            )}
                            {p.dnc && (
                              <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" }}>DNC</span>
                            )}
                          </div>
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "#9ca3af" }}>
                          {p.trade}{p.borough ? ` · ${p.borough}` : ""}
                        </div>
                        <div className="flex items-center justify-between">
                          {/* Left: rating + social icons */}
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            {p.rating && <span className="font-mono text-[10px]" style={{ color: "#6b7280" }}>{p.rating}★</span>}
                            {p.phone && !p.dnc && (isUkMobile(p.phone) ? (
                              <a href={waLink(p.phone, stepFor(p, "whatsapp")?.message)}
                                target="_blank" rel="noopener noreferrer"
                                title="WhatsApp" style={{ color: "#25D366" }} className="hover:opacity-70">
                                <MessageCircle className="w-3 h-3" />
                              </a>
                            ) : (
                              <a href={`tel:${p.phone}`}
                                title="Landline — call" style={{ color: "#9ca3af" }} className="hover:opacity-70">
                                <Phone className="w-3 h-3" />
                              </a>
                            ))}
                            {p.instagram && !p.dnc && (
                              <a href={p.instagram} target="_blank" rel="noopener noreferrer"
                                title="Instagram" style={{ color: "#E1306C" }} className="hover:opacity-70">
                                <Instagram className="w-3 h-3" />
                              </a>
                            )}
                            {p.facebook && !p.dnc && (
                              <a href={p.facebook} target="_blank" rel="noopener noreferrer"
                                title="Facebook" style={{ color: "#1877F2" }} className="hover:opacity-70">
                                <Facebook className="w-3 h-3" />
                              </a>
                            )}
                            {p.dnc && <span title="Opted out — do not contact" className="font-mono text-[9px]" style={{ color: "#ef4444" }}>no contact</span>}
                          </div>
                          {/* Right: stage arrows */}
                          <div className="flex items-center">
                            <button onClick={e => moveStage(e, p, -1)} disabled={BOARD_STAGE_IDS.indexOf(p.stage) === 0}
                              className="p-0.5 disabled:opacity-0 hover:opacity-60" style={{ color: "#9ca3af" }}>
                              <ChevronLeft className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={e => moveStage(e, p, 1)} disabled={BOARD_STAGE_IDS.indexOf(p.stage) === BOARD_STAGE_IDS.length - 1}
                              className="p-0.5 disabled:opacity-0 hover:opacity-60" style={{ color: "#9ca3af" }}>
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Card footer: links or build */}
                      <div className="px-3 py-1.5 border-t" style={{ borderColor: "#f3f4f6" }} onClick={e => e.stopPropagation()}>
                        {p.demo_id ? (
                          <div className="flex items-center gap-3">
                            <Link to={`/demo/${p.demo_id}`}
                              className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest hover:opacity-70"
                              style={{ color: "#FF3B30" }}>
                              Pitch <ExternalLink className="w-2.5 h-2.5" />
                            </Link>
                            <a href={p.demo_public_url || getDemoSiteUrl(p.demo_id)} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest hover:opacity-70"
                              style={{ color: "#3b82f6" }}>
                              Site <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                            {p.demo_view_count > 0 && (
                              <span className="inline-flex items-center gap-0.5 font-mono text-[9px]" style={{ color: "#10b981" }}
                                title={p.demo_last_viewed_at ? `Last viewed ${new Date(p.demo_last_viewed_at).toLocaleDateString("en-GB")}` : undefined}>
                                <Eye className="w-2.5 h-2.5" /> {p.demo_view_count}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button disabled={buildingId === p.id || reviewDemo?.prospect?.id === p.id} onClick={() => buildDemo(p)}
                              className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40 hover:opacity-70"
                              style={{ color: "#94a3b8" }}>
                              {buildingId === p.id
                                ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Building…</>
                                : <><Sparkles className="w-2.5 h-2.5" /> Build demo</>}
                            </button>
                            {!p.website && (
                              <button disabled={verifyingId === p.id} onClick={() => verifyWebsite(p)}
                                title="Check whether this business already has a live website"
                                className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40 hover:opacity-70"
                                style={{ color: "#94a3b8" }}>
                                {verifyingId === p.id
                                  ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Checking…</>
                                  : <><Search className="w-2.5 h-2.5" /> Verify</>}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {cards.length === 0 && (
                    <div className="rounded p-4 text-center" style={{ border: "1px dashed #e5e7eb" }}>
                      <span className="font-mono text-[10px]" style={{ color: "#d1d5db" }}>empty</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Table view */}
      {view === "table" && (
        <div className="px-6 py-6 overflow-x-auto">
          <table className="w-full min-w-[1100px]" style={{ borderCollapse: "collapse", backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {["Business", "Stage", "Phone", "Contact name", "Email", "Value (£)", "Demo", "Outreach", ""].map(h => (
                  <th key={h} className="font-mono text-[10px] uppercase tracking-widest text-left px-3 py-2.5"
                    style={{ color: "#9ca3af" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...filteredProspects].sort((a, b) => {
                const stageDiff = STAGE_IDS.indexOf(a.stage) - STAGE_IDS.indexOf(b.stage);
                return stageDiff !== 0 ? stageDiff : (a.name || "").localeCompare(b.name || "");
              }).map(p => (
                <tr key={p.id} onClick={() => openProspect(p)} className="cursor-pointer hover:bg-gray-50"
                  style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-start gap-2">
                      <div>
                        <div className="font-display text-sm leading-tight" style={{ color: "#111827" }}>{p.name}</div>
                        <div className="font-mono text-[9px] uppercase tracking-wider" style={{ color: "#9ca3af" }}>
                          {p.trade}{p.borough ? ` · ${p.borough}` : ""}{p.rating ? ` · ${p.rating}★` : ""}
                        </div>
                      </div>
                      {p.dnc && (
                        <span className="shrink-0 mt-0.5 font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" }}>DNC</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <select value={p.stage} onChange={e => patch(p.id, { stage: e.target.value })}
                      className="px-2 py-1.5 rounded font-mono text-[11px] outline-none"
                      style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}>
                      {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    {p.phone ? (
                      <div className="flex items-center gap-1.5">
                        {p.dnc ? (
                          <span className="font-mono text-[11px]" style={{ color: "#d1d5db" }} title="Opted out — do not contact">{p.phone}</span>
                        ) : (
                          <a href={`tel:${p.phone}`} className="font-mono text-[11px] hover:underline" style={{ color: "#374151" }}>{p.phone}</a>
                        )}
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={isUkMobile(p.phone)
                            ? { backgroundColor: "#dcfce7", color: "#16a34a" }
                            : { backgroundColor: "#f3f4f6", color: "#9ca3af" }}>
                          {isUkMobile(p.phone) ? "Mobile" : "Landline"}
                        </span>
                      </div>
                    ) : <span className="font-mono text-[11px]" style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <input defaultValue={p.contact_name || ""} onBlur={e => e.target.value !== (p.contact_name || "") && patch(p.id, { contact_name: e.target.value })}
                      placeholder="Owner name" className="w-full px-2 py-1.5 rounded text-[11px] font-mono outline-none"
                      style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <input defaultValue={p.contact_email || ""} onBlur={e => e.target.value !== (p.contact_email || "") && patch(p.id, { contact_email: e.target.value })}
                      placeholder="email@co.uk" className="w-full px-2 py-1.5 rounded text-[11px] font-mono outline-none"
                      style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <input type="number" defaultValue={p.value ?? 500}
                      onBlur={e => { const v = parseInt(e.target.value) || 0; v !== (p.value ?? 500) && patch(p.id, { value: v }); }}
                      className="w-20 px-2 py-1.5 rounded text-[11px] font-mono outline-none"
                      style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    {p.demo_id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/demo/${p.demo_id}`} className="font-mono text-[9px] uppercase tracking-widest hover:opacity-70" style={{ color: "#FF3B30" }}>Pitch</Link>
                        <a href={p.demo_public_url || getDemoSiteUrl(p.demo_id)} target="_blank" rel="noopener noreferrer"
                          className="font-mono text-[9px] uppercase tracking-widest hover:opacity-70" style={{ color: "#3b82f6" }}>Site</a>
                        {p.demo_view_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 font-mono text-[9px]" style={{ color: "#10b981" }}
                            title={p.demo_last_viewed_at ? `Last viewed ${new Date(p.demo_last_viewed_at).toLocaleDateString("en-GB")}` : undefined}>
                            <Eye className="w-2.5 h-2.5" /> {p.demo_view_count}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button disabled={buildingId === p.id || reviewDemo?.prospect?.id === p.id} onClick={() => buildDemo(p)}
                          className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40 hover:opacity-70"
                          style={{ color: "#94a3b8" }}>
                          {buildingId === p.id
                            ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Building…</>
                            : <><Sparkles className="w-2.5 h-2.5" /> Build</>}
                        </button>
                        {!p.website && (
                          <button disabled={verifyingId === p.id} onClick={() => verifyWebsite(p)}
                            title="Check whether this business already has a live website"
                            className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40 hover:opacity-70"
                            style={{ color: "#94a3b8" }}>
                            {verifyingId === p.id
                              ? <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Checking…</>
                              : <><Search className="w-2.5 h-2.5" /> Verify</>}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={p.outreach
                        ? { backgroundColor: "#dcfce7", color: "#16a34a" }
                        : { backgroundColor: "#f3f4f6", color: "#9ca3af" }}>
                      {p.outreach ? "Sent" : "Not sent"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {!p.dnc && p.stage !== "declined" && (
                        <button onClick={() => decline(p)} title="Mark as declined — client said no" className="hover:text-[#ef4444]" style={{ color: "#d1d5db" }}>
                          <Ban className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!p.dnc && (
                        <button onClick={() => patch(p.id, { dnc: true })} title="Mark as Do Not Contact (opted out)"
                          className="hover:text-[#ef4444]" style={{ color: "#d1d5db" }}>
                          <span className="font-mono text-[8px] font-bold">DNC</span>
                        </button>
                      )}
                      <button onClick={() => remove(p.id)} title="Delete permanently" className="hover:text-[#FF3B30]" style={{ color: "#d1d5db" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredProspects.length === 0 && (
            <div className="rounded p-8 text-center" style={{ border: "1px dashed #e5e7eb" }}>
              <span className="font-mono text-[10px]" style={{ color: "#d1d5db" }}>
                {prospects.length === 0 ? "No prospects yet" : "No prospects match your filters"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Brief review — gate before the expensive full site build */}
      {reviewDemo && (
        <BriefReviewModal
          demo={reviewDemo.demo}
          businessName={reviewDemo.prospect.name}
          contact={reviewDemo.prospect}
          onApproved={handleBriefApproved}
          onClose={() => setReviewDemo(null)}
        />
      )}

      {/* Detail panel */}
      {/* Add prospect modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setAddOpen(false)}>
          <div className="w-full max-w-sm mx-4 rounded shadow-2xl" style={{ backgroundColor: "#ffffff" }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #f3f4f6" }}>
              <span className="font-mono text-[11px] uppercase tracking-widest" style={{ color: "#374151" }}>Add prospect</span>
              <button onClick={() => setAddOpen(false)} className="hover:opacity-60" style={{ color: "#9ca3af" }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="px-6 py-5 flex flex-col gap-4">
              {[
                { key: "name", label: "Business name *", placeholder: "Sultan Palace" },
                { key: "trade", label: "Trade *", placeholder: "Restaurant" },
                { key: "borough", label: "Area", placeholder: "Westminster" },
                { key: "address", label: "Address", placeholder: "Edgware Road, London" },
                { key: "phone", label: "Phone", placeholder: "+44 20 ..." },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="font-mono text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: "#9ca3af" }}>{label}</label>
                  <input
                    value={addForm[key]}
                    onChange={e => setAddForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    required={key === "name" || key === "trade"}
                    className="w-full px-3 py-2.5 rounded text-sm outline-none"
                    style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
                  />
                </div>
              ))}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest block mb-1.5" style={{ color: "#9ca3af" }}>
                  <Instagram className="w-3 h-3 inline mr-1" style={{ color: "#E1306C" }} />Instagram handle
                </label>
                <div className="flex items-center rounded overflow-hidden" style={{ border: "1px solid #e5e7eb" }}>
                  <span className="px-3 py-2.5 text-sm font-mono" style={{ backgroundColor: "#f3f4f6", color: "#9ca3af", borderRight: "1px solid #e5e7eb" }}>
                    instagram.com/
                  </span>
                  <input
                    value={addForm.instagram}
                    onChange={e => setAddForm(f => ({ ...f, instagram: e.target.value }))}
                    placeholder="sultanpalace.london"
                    className="flex-1 px-3 py-2.5 text-sm outline-none"
                    style={{ backgroundColor: "#f9fafb", color: "#111827" }}
                  />
                </div>
              </div>
              <button type="submit" disabled={addSaving}
                className="w-full py-3 font-display font-bold text-white rounded mt-1 disabled:opacity-50"
                style={{ backgroundColor: "#FF3B30" }}>
                {addSaving ? "Adding…" : "Add to pipeline"}
              </button>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto flex flex-col shadow-2xl rounded-xl"
            style={{ backgroundColor: "#ffffff" }}
            onClick={e => e.stopPropagation()}>

            {/* Panel header */}
            <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
              style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #f3f4f6" }}>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stageColor(selected.stage) }} />
                <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#6b7280" }}>
                  {stageLabel(selected.stage)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                  {!selected.dnc && selected.stage !== "declined" && (
                  <button onClick={() => decline(selected)} title="Mark as declined — client said no" className="hover:text-[#ef4444]" style={{ color: "#d1d5db" }}>
                    <Ban className="w-4 h-4" />
                  </button>
                  )}
                  {!selected.dnc && (
                    <button onClick={() => patch(selected.id, { dnc: true })} title="Mark as Do Not Contact (opted out)"
                      className="hover:text-[#ef4444] font-mono text-[9px] font-bold" style={{ color: "#d1d5db" }}>
                      DNC
                    </button>
                  )}
                <button onClick={() => remove(selected.id)} title="Delete permanently" className="hover:text-[#FF3B30]" style={{ color: "#d1d5db" }}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setSelected(null)} className="hover:opacity-60" style={{ color: "#9ca3af" }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="px-6 py-6 flex flex-col gap-5 flex-1">
              {/* Business info */}
              <div>
                <div className="flex items-start gap-2 mb-1">
                  <h2 className="font-display text-2xl tracking-tight" style={{ color: "#111827" }}>{selected.name}</h2>
                  {selected.dnc && (
                    <span className="shrink-0 mt-1 font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
                      style={{ backgroundColor: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" }}>DNC</span>
                  )}
                </div>
                <p className="font-mono text-[11px] uppercase tracking-wider" style={{ color: "#9ca3af" }}>
                  {selected.trade}{selected.borough ? ` · ${selected.borough}` : ""}
                </p>
                {selected.address && <p className="text-sm mt-1" style={{ color: "#6b7280" }}>{selected.address}</p>}
                <div className="flex items-center gap-4 mt-3">
                  {selected.rating && (
                    <span className="text-sm" style={{ color: "#374151" }}>{selected.rating}★ · {selected.reviews} reviews</span>
                  )}
                  {selected.phone && (selected.dnc ? (
                    <span className="flex items-center gap-1 text-sm font-mono" style={{ color: "#d1d5db" }} title="Opted out — do not contact">
                      <Phone className="w-3 h-3" /> {selected.phone}
                    </span>
                  ) : (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-1 text-sm font-mono hover:underline" style={{ color: "#FF3B30" }}>
                      <Phone className="w-3 h-3" /> {selected.phone}
                    </a>
                  ))}
                  {selected.phone && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={isUkMobile(selected.phone)
                        ? { backgroundColor: "#dcfce7", color: "#16a34a" }
                        : { backgroundColor: "#f3f4f6", color: "#9ca3af" }}>
                      {isUkMobile(selected.phone) ? "Mobile" : "Landline"}
                    </span>
                  )}
                </div>
              </div>

              {/* Demo links */}
              {selected.demo_id && (
                <div className="rounded p-4" style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#9ca3af" }}>Links</p>
                    <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={isPublished
                        ? { backgroundColor: "#dcfce7", color: "#16a34a" }
                        : { backgroundColor: "#f3f4f6", color: "#9ca3af" }}>
                      {isPublished ? "Published" : "Publishing…"}
                    </span>
                    {selected.demo_view_count > 0 && (
                      <span className="inline-flex items-center gap-1 font-mono text-[9px] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
                        title={selected.demo_last_viewed_at ? `Last viewed ${new Date(selected.demo_last_viewed_at).toLocaleDateString("en-GB")}` : undefined}>
                        <Eye className="w-2.5 h-2.5" /> {selected.demo_view_count} view{selected.demo_view_count !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {[
                      { label: "Pitch page", href: `${window.location.origin}/demo/${selected.demo_id}`, color: "#FF3B30" },
                      { label: "Generated site", href: getDemoPublicUrl(selectedDemo, selected.demo_id), color: "#3b82f6" },
                    ].map(({ label, href, color }) => (
                      <div key={label} className="flex items-center gap-2">
                        <a href={href} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 font-mono text-xs hover:underline" style={{ color }}>
                          <ExternalLink className="w-3 h-3" /> {label}
                        </a>
                        <button onClick={() => { navigator.clipboard.writeText(href); toast.success(`${label} copied`); }}
                          className="flex items-center gap-1 font-mono text-[10px] px-2 py-1 rounded hover:bg-gray-100"
                          style={{ color: "#6b7280", border: "1px solid #e5e7eb" }}>
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stage */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest block mb-2" style={{ color: "#9ca3af" }}>Stage</label>
                <select value={selected.stage} onChange={e => patch(selected.id, { stage: e.target.value })}
                  className="w-full px-3 py-2.5 rounded font-mono text-sm outline-none"
                  style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}>
                  {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>

              {/* Value */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest block mb-2" style={{ color: "#9ca3af" }}>Deal value (£)</label>
                <input type="number" value={value} onChange={e => setValue(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 rounded font-mono text-sm outline-none"
                  style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest block mb-2" style={{ color: "#9ca3af" }}>Contact name</label>
                  <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Owner name"
                    className="w-full px-3 py-2.5 rounded text-sm outline-none"
                    style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest block mb-2" style={{ color: "#9ca3af" }}>Email</label>
                  <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="email@co.uk"
                    className="w-full px-3 py-2.5 rounded text-sm outline-none"
                    style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                </div>
              </div>

              {/* Socials */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest block mb-2" style={{ color: "#9ca3af" }}>Socials</label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Instagram className="w-4 h-4 flex-shrink-0" style={{ color: "#E1306C" }} />
                    <input value={instagram} onChange={e => setInstagram(e.target.value)}
                      placeholder="https://instagram.com/..." className="flex-1 px-3 py-2 rounded text-sm outline-none"
                      style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 flex-shrink-0" style={{ color: "#1877F2" }} />
                    <input value={facebook} onChange={e => setFacebook(e.target.value)}
                      placeholder="https://facebook.com/..." className="flex-1 px-3 py-2 rounded text-sm outline-none"
                      style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
                  </div>
                </div>
              </div>

              {/* Outreach */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#9ca3af" }}>Outreach</label>
                  <button onClick={handleGenerateOutreach} disabled={generatingOutreach || selected.dnc || !selected.demo_id || !hasOutreachChannel || !isPublished}
                    title={
                      selected.dnc ? "This lead has opted out (DNC) — no further outreach can be generated"
                      : !selected.demo_id ? "Build a demo first — outreach copy links to it"
                      : !hasOutreachChannel ? "No phone number, Instagram, Facebook, or email on file for this lead"
                      : !isPublished ? "Site not published to the CDN yet — check server logs if this persists"
                      : undefined
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50 transition-colors"
                    style={{ backgroundColor: "#111827", color: "#ffffff" }}>
                    {generatingOutreach
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating…</>
                      : <><Sparkles className="w-3 h-3" /> {selected.outreach ? "Regenerate" : "Generate"}</>}
                  </button>
                </div>

                {selected.dnc && (
                  <p className="font-mono text-[10px] mb-3" style={{ color: "#ef4444" }}>
                    This lead opted out — do not call, message, or generate further outreach.
                  </p>
                )}

                {!selected.dnc && !selected.demo_id && (
                  <p className="font-mono text-[10px] mb-3" style={{ color: "#f59e0b" }}>
                    Build a demo before generating outreach — the message links to it.
                  </p>
                )}

                {selected.demo_id && !hasOutreachChannel && (
                  <p className="font-mono text-[10px] mb-3" style={{ color: "#f59e0b" }}>
                    No phone number, Instagram, Facebook, or email on file for this lead.
                  </p>
                )}

                {selected.demo_id && hasOutreachChannel && !isPublished && (
                  <p className="font-mono text-[10px] mb-3" style={{ color: "#f59e0b" }}>
                    Site still publishing to the CDN — outreach link isn't ready yet.
                  </p>
                )}

                {selected.outreach?.sequence?.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {selected.outreach.sequence.map((step, i) => {
                      const meta = CHANNEL_META[step.channel] || CHANNEL_META.call;
                      const Icon = meta.icon;
                      const openLink = channelOpenLink(step.channel, outreachWithLink(step.message));
                      const isDay0 = i === 0;
                      const nextStep = isDay0 ? selected.outreach.sequence[1] : null;
                      return (
                        <div key={`${step.channel}-${i}`} className="rounded" style={{ border: "1px solid #e5e7eb" }}>
                          <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid #f3f4f6" }}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                              <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#6b7280" }}>
                                {meta.label}
                              </span>
                              <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ backgroundColor: "#f3f4f6", color: "#9ca3af" }}>
                                {isDay0 ? "Day 0 · first touch" : `Day ${step.day} · follow-up ${i}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => { navigator.clipboard.writeText(outreachWithLink(step.message)); toast.success("Copied"); }}
                                className="font-mono text-[10px] px-2 py-0.5 hover:bg-gray-100 rounded" style={{ color: "#6b7280", border: "1px solid #e5e7eb" }}>
                                <Copy className="w-3 h-3" />
                              </button>
                              {openLink && (
                                <a href={openLink} target="_blank" rel="noopener noreferrer"
                                  className="font-mono text-[10px] px-2 py-0.5 hover:opacity-80 rounded"
                                  style={{ backgroundColor: meta.color, color: "#ffffff" }}>
                                  Open →
                                </a>
                              )}
                              {/* Calendar button: follow-up steps get a dropdown; Day 0 gets a shortcut to add the next follow-up */}
                              {!isDay0 && (
                                <CalendarDropdown
                                  step={step}
                                  prospectName={selected.name}
                                  borough={selected.borough}
                                  message={outreachWithLink(step.message)}
                                />
                              )}
                            </div>
                          </div>
                          <pre className="px-3 py-3 text-xs whitespace-pre-wrap font-sans leading-relaxed" style={{ color: "#374151" }}>
                            <HighlightYourName text={outreachWithLink(step.message)} />
                          </pre>
                          {/* Day 0 shortcut: remind me to do the next follow-up */}
                          {isDay0 && nextStep && (
                            <div className="px-3 py-2 flex items-center gap-2" style={{ borderTop: "1px solid #f3f4f6", backgroundColor: "#fafafa" }}>
                              <CalendarPlus className="w-3 h-3 flex-shrink-0" style={{ color: "#6b7280" }} />
                              <span className="font-mono text-[10px]" style={{ color: "#9ca3af" }}>
                                Sending this? Remind yourself to follow up on Day {nextStep.day}:
                              </span>
                              <button
                                onClick={() => openGoogleCalendar(buildCalendarEvent(nextStep, selected.name, selected.borough, outreachWithLink(nextStep.message)))}
                                className="font-mono text-[10px] px-2 py-0.5 hover:bg-blue-50 rounded"
                                style={{ color: "#4285F4", border: "1px solid #e5e7eb" }}
                              >
                                Google Cal
                              </button>
                              <button
                                onClick={() => { downloadIcs(buildCalendarEvent(nextStep, selected.name, selected.borough, outreachWithLink(nextStep.message))); toast.success("Calendar file downloaded"); }}
                                className="font-mono text-[10px] px-2 py-0.5 hover:bg-gray-100 rounded"
                                style={{ color: "#6b7280", border: "1px solid #e5e7eb" }}
                              >
                                .ics
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded p-4 text-center font-mono text-[11px]"
                    style={{ border: "1px dashed #e5e7eb", color: "#9ca3af" }}>
                    Click Generate to write a multi-touch campaign across the channels available
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest block mb-2" style={{ color: "#9ca3af" }}>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="What came up on the call? What did they like? Any objections?"
                  rows={5} className="w-full px-3 py-2.5 rounded text-sm outline-none resize-none"
                  style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }} />
              </div>
            </div>

            {/* Save */}
            <div className="px-6 py-4 sticky bottom-0" style={{ backgroundColor: "#ffffff", borderTop: "1px solid #f3f4f6" }}>
              <button onClick={savePanel} disabled={saving}
                className="w-full py-3 font-display font-bold text-white rounded transition-colors disabled:opacity-50"
                style={{ backgroundColor: "#FF3B30" }}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
